// Library Methods
import { useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import ProgressBar from "src/components/shared/displays/ProgressBar/ProgressBar";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Constants
import { DeviceStagesTimeline } from "src/constants/enums";

// Utils
import { getDeviceStagesTimelineText } from "src/utils/signalR/signalRHelper";

const LiveUpdateStepper = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);

  const {
    examStatus,
    examReportLive,
    activeStep,
    possibleSteps,
    percentage,
    isConfirmation,
    isNotStarted,
    isRightCompleted,
    isTerminated,
    actions,
  } = useLiveUpdateContext();
  const { setPercentage, setExamReportLive } = actions;

  const allExamSteps = useMemo(
    () =>
      possibleSteps.map((stage) => {
        // if the exam is at the Exam stage, we also display the status (as it may be In Progress or Paused)
        const isExamOrTutorial =
          (stage === DeviceStagesTimeline.Exam ||
            stage === DeviceStagesTimeline.Tutorial) &&
          activeStep.value === stage;

        return {
          title: getDeviceStagesTimelineText({
            stage,
            isExamOrTutorial,
            extraExamWord: examStatus?.extraWord ?? examStatus?.status,
            t,
          }),
        };
      }),
    [activeStep, examStatus, possibleSteps, t]
  );

  useEffect(() => {
    setPercentage(
      Math.floor(
        examReportLive?.stats?.progress
          ? examReportLive.stats.progress * 100
          : examReportLive?.Progress
            ? examReportLive?.Progress * 100
            : 0
      )
    );
  }, [
    setPercentage,
    examReportLive?.stats?.progress,
    examReportLive?.Progress,
  ]);

  useEffect(() => {
    if (isConfirmation || isNotStarted || isRightCompleted || isTerminated) {
      setPercentage(0);
      setExamReportLive(undefined);
    }
  }, [
    isConfirmation,
    isRightCompleted,
    isNotStarted,
    isTerminated,
    setPercentage,
    setExamReportLive,
  ]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <Grid
        display="flex"
        flexWrap="wrap"
        alignContent="center"
        justifyContent={{ xs: "space-around", sm: "space-between" }}
        gap="8px"
      >
        {allExamSteps.map((step, index) => {
          const isActiveStep = activeStep.index === index;
          const isCompletedStep =
            index < activeStep.index && activeStep.index !== -1;

          return (
            <Box
              key={step.title}
              flex={1}
              minWidth="fit-content"
              display="flex"
              justifyContent="center"
              alignItems="center"
              borderRadius="4px"
              sx={{
                p: "4px 8px",
                backgroundColor: isActiveStep
                  ? "#E2772E"
                  : isCompletedStep
                    ? "#202338"
                    : "#5B6D7A20",
              }}
            >
              <Typography
                fontSize="12px"
                color={isActiveStep || isCompletedStep ? "#FFF" : onSurface}
              >
                {index + 1}. {step.title}
              </Typography>
            </Box>
          );
        })}
      </Grid>

      <ProgressBar value={percentage} />
    </Box>
  );
};

export default LiveUpdateStepper;
